import { DestroyRef, inject, Injectable } from '@angular/core';

import { WINDOW } from '@rpgc-ai/shared-interfaces/tokens/window.token';

import type { SpeechBackendCallbacks, SpeechRecognitionBackend } from './speech-backend';

type SpeechRecognitionConstructor = new () => SpeechRecognition;

interface SpeechRecognitionAlternative {
  readonly transcript: string;
}
interface SpeechRecognitionResult {
  readonly [index: number]: SpeechRecognitionAlternative;
  readonly isFinal: boolean;
}
interface SpeechRecognitionResults {
  readonly [index: number]: SpeechRecognitionResult;
  readonly length: number;
}
interface SpeechRecognitionEvent {
  readonly resultIndex: number;
  readonly results: SpeechRecognitionResults;
}
interface SpeechRecognitionErrorEvent {
  readonly error: string;
}
interface SpeechRecognition {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onstart: (() => void) | null;
  onend: (() => void) | null;
  onerror: ((event: SpeechRecognitionErrorEvent) => void) | null;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onspeechend: (() => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}

declare global {
  interface Window {
    SpeechRecognition?: SpeechRecognitionConstructor;
    webkitSpeechRecognition?: SpeechRecognitionConstructor;
  }
}

@Injectable()
export class WebSpeechBackend implements SpeechRecognitionBackend {
  readonly #window = inject(WINDOW);
  readonly #destroyRef = inject(DestroyRef);

  // Web Speech runs continuously; the facade owns silence-based auto-stop.
  readonly singleShot = false;
  // Coarse pointer ⇒ mobile web/TMA: interim chunks REPLACE (last wins) instead
  // of concatenating, matching the pre-refactor service behavior.
  readonly #isMobileBrowser = this.#window?.matchMedia('(pointer: coarse)').matches ?? false;

  #recognition: SpeechRecognition | null = null;
  #owner: symbol | null = null;
  #callbacks: SpeechBackendCallbacks | null = null;
  readonly #processedIndices = new Set<number>();
  // Suppresses a finalized segment whose text repeats the previous final, even at
  // a new result index (ported from the pre-refactor service).
  #lastFinalSegment = '';

  constructor() {
    this.#destroyRef.onDestroy(() => this.dispose());
  }

  async isAvailable(): Promise<boolean> {
    return this.#getCtor() !== undefined;
  }

  async ensurePermission(): Promise<boolean> {
    const permissions = this.#window?.navigator?.permissions;
    if (!permissions) {
      return true;
    }
    try {
      const status = await permissions.query({ name: 'microphone' as PermissionName });
      return status.state !== 'denied';
    } catch {
      // Permissions API unsupported / sandboxed — proceed and rely on onerror.
      return true;
    }
  }

  start(lang: string, ownerId: symbol, callbacks: SpeechBackendCallbacks): void {
    if (this.#owner !== null) {
      return;
    }
    const Ctor = this.#getCtor();
    if (!Ctor) {
      return;
    }

    this.#owner = ownerId;
    this.#callbacks = callbacks;
    this.#processedIndices.clear();
    this.#lastFinalSegment = '';

    const recognition = new Ctor();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = lang;
    this.#attachHandlers(recognition);
    this.#recognition = recognition;
    try {
      recognition.start();
    } catch {
      // Web Speech throws if start() is called in a bad state — release the owner
      // so a later start() can retry. Preserves the pre-refactor no-throw behavior.
      this.#callbacks?.onError('aborted');
      this.#hardStop();
    }
  }

  stop(ownerId: symbol): void {
    if (this.#owner !== ownerId) {
      return;
    }
    // Graceful stop: the recognition fires onend, which releases the session so
    // the same facade can start() again.
    this.#recognition?.stop();
  }

  abort(ownerId: symbol): void {
    if (this.#owner !== ownerId) {
      return;
    }
    this.#hardStop();
  }

  release(ownerId: symbol): void {
    if (this.#owner !== ownerId) {
      return;
    }
    this.#hardStop();
  }

  dispose(): void {
    this.#hardStop();
  }

  #getCtor(): SpeechRecognitionConstructor | undefined {
    const win = this.#window;
    if (!win) {
      return undefined;
    }
    return win.SpeechRecognition ?? win.webkitSpeechRecognition;
  }

  #attachHandlers(recognition: SpeechRecognition): void {
    recognition.onstart = () => {
      this.#processedIndices.clear();
      this.#lastFinalSegment = '';
      this.#callbacks?.onStart();
    };
    recognition.onend = () => {
      // Natural end (user stop / silence). Dispatch onEnd, then release the
      // session WITHOUT calling abort() — the instance already ended.
      this.#callbacks?.onEnd();
      this.#clearSession();
    };
    recognition.onerror = (event) => {
      this.#callbacks?.onError(event.error);
    };
    recognition.onresult = (event) => {
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        const text = result[0].transcript;
        if (result.isFinal) {
          // Index-dedup AND text-dedup: skip an already-seen index or a final whose
          // text repeats the previous final segment.
          if (this.#processedIndices.has(i) || text === this.#lastFinalSegment) {
            continue;
          }
          this.#processedIndices.add(i);
          this.#lastFinalSegment = text;
          this.#callbacks?.onResult({ transcript: text, isFinal: true });
        } else {
          // Mobile: last interim wins (replace). Desktop: concatenate the batch.
          interim = this.#isMobileBrowser ? text : `${interim}${text}`;
        }
      }
      if (interim) {
        this.#callbacks?.onResult({ transcript: interim, isFinal: false });
      }
    };
    recognition.onspeechend = () => {
      // Reset the facade's silence timer when speech ends without changing state.
      this.#callbacks?.onSpeechEnd();
    };
  }

  // Abort a live instance, then release. Detaches handlers BEFORE abort() so the
  // abort-triggered onend cannot re-enter and dispatch a stray onEnd.
  #hardStop(): void {
    const recognition = this.#recognition;
    if (recognition) {
      recognition.onstart = null;
      recognition.onend = null;
      recognition.onerror = null;
      recognition.onresult = null;
      recognition.onspeechend = null;
      recognition.abort();
    }
    this.#clearSession();
  }

  // Drops all session state so the next start() acquires a fresh owner.
  #clearSession(): void {
    this.#recognition = null;
    this.#owner = null;
    this.#callbacks = null;
    this.#processedIndices.clear();
  }
}
